import { typography, radius } from '@/shared/theme/tokens';
import { HUD } from './hudTheme';
import { GlassPanel } from './GlassPanel';

const MONO = typography.fontFamily.mono;

/**
 * Status semaphore — the launch-control GO/HOLD light. The dominant glance
 * signal: a large glowing lamp driven by the adaptive headline (state/label/
 * count/subtext), with a status-code chip. Pulses only when it demands
 * attention (critical/degraded) and only when motion is allowed.
 */
export function Semaphore({ headline, animate }) {
  const c = headline.color;
  const alarm = headline.state === 'critical' || headline.state === 'degraded';

  return (
    <GlassPanel accent={c} glow={alarm ? c : undefined} style={{ flex: 1 }}>
      {/* eyebrow */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
        borderBottom: `1px solid ${HUD.strokeSoft}`, flexShrink: 0,
      }}>
        <span style={{
          fontFamily: MONO, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold,
          letterSpacing: typography.letterSpacing.widest, textTransform: 'uppercase', color: HUD.inkFaint,
        }}>
          System Status
        </span>
        <span style={{
          marginLeft: 'auto', fontFamily: MONO, fontSize: typography.fontSize.xs,
          fontWeight: typography.fontWeight.bold, letterSpacing: typography.letterSpacing.wider,
          color: c, background: `${headline.bgDark}`, border: `1px solid ${headline.border}`,
          borderRadius: radius.xs, padding: '2px 8px',
        }}>
          {headline.code}
        </span>
      </div>

      {/* lamp */}
      <div style={{
        flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 16, padding: '20px 16px',
      }}>
        <Lamp color={c} icon={headline.icon} alarm={alarm} animate={animate} />

        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{
            fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize['7xl'],
            fontWeight: typography.fontWeight.extrabold, letterSpacing: typography.letterSpacing.wide,
            color: c, lineHeight: 1,
          }}>
            {headline.label}
          </div>
          {headline.count > 0 && (
            <div style={{
              fontFamily: MONO, fontSize: typography.fontSize['4xl'], fontWeight: typography.fontWeight.bold,
              color: c, fontVariantNumeric: 'tabular-nums', lineHeight: 1,
            }}>
              ×{headline.count}
            </div>
          )}
          {headline.subtext && (
            <div style={{
              fontFamily: MONO, fontSize: typography.fontSize.xs, color: HUD.inkFaint,
              letterSpacing: typography.letterSpacing.wide, fontVariantNumeric: 'tabular-nums',
              marginTop: 2,
            }}>
              {headline.subtext}
            </div>
          )}
        </div>
      </div>
    </GlassPanel>
  );
}

function Lamp({ color, icon, alarm, animate }) {
  return (
    <div style={{
      position: 'relative', width: 132, height: 132, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {/* outer halo */}
      <span aria-hidden style={{
        position: 'absolute', inset: -6, borderRadius: radius.full,
        background: `radial-gradient(circle, ${color} 0%, transparent 68%)`,
        opacity: 0.22,
        animation: animate && alarm ? 'hud-pulse 1.3s steps(1, end) infinite' : 'none',
      }}
      />
      {/* ring */}
      <span aria-hidden style={{
        position: 'absolute', inset: 0, borderRadius: radius.full,
        border: `2px solid ${color}`, boxShadow: `0 0 22px -2px ${color}, inset 0 0 22px -8px ${color}`,
      }}
      />
      {/* lamp face */}
      <span style={{
        position: 'relative', width: 96, height: 96, borderRadius: radius.full,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `radial-gradient(circle at 50% 38%, ${color}, ${HUD.void} 130%)`,
        boxShadow: `0 0 26px -6px ${color}`,
        animation: animate && alarm ? 'hud-glow 1.3s ease-in-out infinite' : 'none',
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 46, color: HUD.ink }}>
          {icon}
        </span>
      </span>
    </div>
  );
}
